<div id="app">
  <div class="p-2">
    <!-- TEMPLATE SELECTOR -->
    <div class="mb-4">
      <!-- Empty state -->
      <div
        v-if="assigned_templates.length === 0"
        class="text-xs text-muted p-3 text-center"
      >
        No templates assigned to this entity type yet.
      </div>

      <!-- Full-width stacked cards -->
      <div v-else class="d-flex flex-column gap-2">
        <div
          v-for="template in assigned_templates"
          :key="template.id"
          class="w-100"
        >
          <!-- CARD -->
          <div
            class="template-shell"
            :class="{ 'template-shell-selected': selected_template_id === template.template_id }"
          >
            <!-- SIGNING PROGRESS (INSPO TIMELINE) -->
            <div
              v-if="selected_template_id === template.template_id && local_steps.length > 0"
              class="progress-panel"
            >
              <div class="progress-panel-header">
                <div>
                  <p class="text-x text-dark mb-0">Signing Progress</p>
                  <p class="text-xs text-dark mb-0">Track the status of each trust</p>
                </div>

                <div class="progress_panel_meta">
                  <span class="progress-panel-count text-xs">
                    {{ local_steps.filter(s => get_step_overall_status(s) === 'completed').length }}
                    / {{ local_steps.length }} completed
                  </span>
                </div>
              </div>

              <div class="progress-timeline mt-4 mb-4">
                <div
                  v-for="(step, index) in local_steps"
                  :key="'pt-' + step.id"
                  class="progress-item"
                >
                  <!-- connector line -->
                  <div
                    v-if="index < local_steps.length - 1"
                    class="progress-line"
                    :class="get_step_overall_status(step) === 'completed' ? 'progress-line-done' : 'progress-line-todo'"
                  ></div>

                  <!-- circle -->
                  <div
                    class="progress-node"
                    :class="'progress-node-' + normalise_status(get_step_overall_status(step))"
                  >
                    <i class="fs-6" :class="get_step_status_icon_class(get_step_overall_status(step))"></i>
                  </div>

                  <div class="progress-label text-xs text-truncate" :title="step.title">
                    {{ step.title }}
                  </div>

                  <!-- PERSON NAME -->
                  <div
                    v-if="get_step_primary_name(step)"
                    class=" text-muted text-xs text-truncate"
                    :title="get_step_primary_name(step)"
                  >
                    {{ get_step_primary_name(step) }}
                  </div>

                  <div
                    class="progress-badge text-xxs"
                    :class="'progress-badge-' + normalise_status(get_step_overall_status(step))"
                  >
                    {{ format_status_label(get_step_overall_status(step)) }}
                  </div>
                </div>
              </div>
            </div>
            <!-- /SIGNING PROGRESS -->

            <!-- TEMPLATE CARD (INSPO) -->
            <div
              class="glassy-bg rounded border-0 w-100"
              role="button"
              @click="handle_template_click(template)"
            >
              <div class="card-body">
                <div class="template-card-header">
                  <!-- LEFT -->
                  <div class="d-flex align-items-start gap-3 flex-grow-1">
                    <div class="">
                      <i class="bi bi-file-earmark-text fs-4"></i>
                    </div>

                    <div class="flex-grow-1">
                      <div class=" fw-bold">Assigned Template</div>

                      <div class="template_title text-xs">
                        {{ template.template_name }}
                      </div>

                      <div class="text-xs text-dark">
                        Document submission status
                      </div>

                      <div
                        v-if="selected_template_id === template.template_id"
                        class="template-helper text-xs text-dark"
                      >
                        <i class="bi bi-info-circle"></i>
                        <span>View the progress timeline above, then manage each step below</span>
                      </div>
                    </div>
                  </div>

                  <!-- RIGHT -->
                  <div class="template-actions">
                    <!-- Status pill -->
                    <span
                      class="status-pill text-xs text-dark border-1 border-dark"
                      :class="'status-pill-' + normalise_status(get_template_status_label(template))"
                    >
                      {{ get_template_status_label(template) }}
                    </span>

                    <!-- Download -->
                    <button
                      v-if="role !== 'Entity' && normalise_status(get_template_status_label(template)) === 'completed'"
                      class="btn btn-primary-dark text-xxs btn-sm shadow-sm"
                      @click.stop="download_signed_document(template.submission_id)"
                    >
                      Download PDF
                    </button>
                  </div>
                </div>

                <!-- FOOTER -->
                <div class="template-card-footer">
                  <div class="template-hint">
                    <i class="bi bi-diagram-3"></i>
                    <span>Signing flow timeline will show below once selected</span>
                  </div>

                  <div class="d-flex align-items-center gap-2">
                    <button
                      v-if="role !== 'Entity'"
                      type="button"
                      class="btn btn-primary-dark text-xxs btn-sm shadow-sm"
                      @click.stop="handle_template_click(template)"
                    >
                      View Signing Flow
                    </button>

                    <button
                      v-if="role !== 'Entity' && normalise_status(get_template_status_label(template)) !== 'completed'"
                      type="button"
                      class="btn btn-primary-dark text-xxs btn-sm shadow-sm"
                      @click.stop="preview_template(template)"
                      data-bs-toggle="modal"
                      data-bs-target="#preview_template_modal"
                    >
                      <span v-if="!is_preview_loading">Preview</span>
                      <span v-else>Loading…</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- /TEMPLATE CARD -->

            <!-- VERTICAL TIMELINE -->
            <div
              class="signing-timeline-wrap"
              v-if="selected_template_id === template.template_id && local_steps.length > 0"
            >
              <div class="signing-timeline mt-3">
                <div v-for="(step, step_index) in local_steps" :key="step.id" class="mb-3">
                  <div
                    class="signing-timeline-node"
                    :style="{ top: (node_top_offsets[step_index] ?? 0) + 'px' }"
                  >
                    <span class="signing-node-index">{{ step_index + 1 }}</span>
                  </div>

                  <div
                    class="signing-timeline-arrow"
                    :style="{ top: (node_top_offsets[step_index] ?? 0) + 'px' }"
                    v-if="step_index < local_steps.length - 1"
                  ></div>

                  <div
                    class="signing-step-card border-0 p-2"
                    :class="{ active: step.state === 'current' || step.expanded }"
                    :ref="(element) => (step_element_refs[step_index] = element)"
                    @click="toggle_expand(step)"
                  >
                    <div class="d-flex align-items-start">
                      <div class="flex-grow-1">
                        <div class="d-flex align-items-center justify-content-between">
                          <div class="signing-step-badge text-uppercase text-xxs text-dark">
                            Step {{ step_index + 1 }}
                          </div>

                          <div class="d-flex align-items-center gap-2">
                            <button
                              v-if="role !== 'Entity'"
                              type="button"
                              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                              data-bs-toggle="modal"
                              data-bs-target="#assign_users_to_workflow"
                              @click.stop="open_manage(step)"
                            >
                              Manage
                            </button>

                          </div>
                        </div>

                        <div class="mt-1 fw-500">{{ step.title }}</div>

                        <div class="mt-2 d-flex align-items-center gap-3 flex-wrap">
                          <span class="signing-signers-count text-xs text-dark border border-3 rounded-lg p-1">
                            {{ step.signers.length }} signers assigned
                          </span>

                          <div class="d-flex gap-2 flex-wrap">
                            <span
                              class="signing-signer-chip border border-3 rounded-lg text-dark text-xs p-1"
                              v-for="(signer_name, signer_index) in step.signers"
                              :key="signer_index"
                            >
                              <span class="signing-chip-dot"></span>
                              {{ signer_name }}
                            </span>
                          </div>
                        </div>

                        <div class="text-dark text-xs mt-2" v-if="step.owner && !step.expanded">
                          {{ step.owner }}
                        </div>

                        <div
                          v-if="step.expanded && step.signers_meta && step.signers_meta.length > 0"
                          class="mt-3 pt-3 border-top border-dark"
                        >
                          <div class="text-xs text-dark fw-bold mb-2">
                            Assigned signers details
                          </div>

                          <div
                            v-for="(signer, signer_index) in step.signers_meta"
                            :key="'meta-' + signer_index"
                            class="signing-signer-row d-flex align-items-center justify-content-between mb-2 shadow"
                          >
                            <div class="d-flex align-items-center gap-2">
                              <i class="fs-4 ":class="get_submitter_icon_class(signer.status_label)"></i>
                              <div>
                                <div class="d-flex align-items-center gap-1 mb-1">
                                  <i class="bi bi-person-circle small"></i>
                                  <span class="text-xs fw-500">{{ signer.name }}</span>
                                </div>
                                <div class="text-xxs text-dark">{{ signer.email }}</div>
                              </div>
                            </div>

                            <div class="d-flex align-items-center gap-2">
                              <span
                                class="status-badge rounded-pill text-xxs signing-status-badge"
                                :class="get_submitter_badge_class(signer.status_label)"
                              >
                                <!-- {{ signer.docuseal_status_label }} -->
                                {{ format_status_label(signer.docuseal_status_label) }}

                              </span>

                              <button
                                v-if="role !== 'Entity' && ['sent','opened','awaiting','pending'].includes(normalise_status(signer.docuseal_status_label))"
                                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                                @click.stop="resend_submitter_email(signer)"
                              >
                                Resend Email
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- /VERTICAL TIMELINE -->
          </div>
          <!-- /CARD -->
        </div>
        <!-- /v-for wrapper -->
      </div>
    </div>
    <!-- /TEMPLATE SELECTOR -->

    <!-- MANAGE MODAL -->
    <teleport to="body">
      <div
        class="modal fade"
        id="assign_users_to_workflow"
        tabindex="-1"
        aria-labelledby="assign_users_to_workflow_modal_label"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content border-0 shadow-lg">
            <!-- Header -->
            <div
              class="modal-header cute-header d-flex align-items-center justify-content-between px-3 py-2"
            >
              <h5
                class="modal-title text-white text-xxs m-0"
                id="assign_users_to_workflow_modal_label"
              >
                Assign User To Workflow
              </h5>
              <button
                type="button"
                class="btn-close modal-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <!-- Body -->
            <div class="modal-body p-3">
              <!-- Only render table when we have real rows -->
              <div
                class="table-responsive"
                v-if="assignable_user_rows.length > 0"
              >
                <table
                  class="table table-hover"
                  id="assign_users_table"
                  ref="assignUsersTable"
                  :key="assign_users_table_key"
                >
                  <thead class="custom-header">
                    <tr>
                      <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                        No
                      </th>
                      <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                        First Name
                      </th>
                      <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                        Last Name
                      </th>
                      <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                        Email
                      </th>
                      <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                        Assign
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(user, index) in assignable_user_rows"
                      :key="user.id"
                      class="text-xxs"
                    >
                      <td class="text-xs text-start">{{ index + 1 }}</td>
                      <td class="text-xs text-start">
                        {{ user.first_name || '' }}
                      </td>
                      <td class="text-xs text-start">
                        {{ user.last_name || '' }}
                      </td>
                      <td class="text-xs text-start">
                        {{ user.email || '—' }}
                      </td>
                      <td class="text-xs text-start">
                        <div
                          class="d-flex justify-content-center align-items-center gap-2"
                        >
                          <div class="form-check form-switch m-0">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              role="switch"
                              :id="'assign-toggle-' + user.id"
                              v-model="user.is_assigned"
                              :disabled="user.is_loading"
                            />
                          </div>
                          <span
                            class="badge"
                            :class="user.is_assigned ? 'bg-success' : 'bg-secondary'"
                          >
                            {{ user.is_assigned ? 'Assigned' : 'Unassigned' }}
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Footer -->
            <div
              class="modal-footer border-0 p-2 d-flex justify-content-end"
            >
              <button
                type="button"
                class="btn btn-sm btn-primary-dark text-xxs"
                @click="save_assignments"
              >
                Save Assignment(s)
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <!-- PREVIEW MODAL -->
    <teleport to="body">
      <div
        class="modal fade"
        id="preview_template_modal"
        tabindex="-1"
        aria-labelledby="preview_template_modal_label"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-xl modal-dialog-centered">
          <div class="modal-content border-0 shadow-lg">
            <!-- Header -->
            <div class="modal-header cute-header d-flex align-items-center justify-content-between px-3 py-2">
              <h5 class="modal-title text-white text-xs m-0">
                Document Preview
              </h5>
              <button
                type="button"
                class="btn-close modal-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <!-- Body -->
            <div class="modal-body p-0">
              <!-- Loading State -->
              <div v-if="is_preview_loading" class="text-center py-5">
                <div class="spinner-border spinner-border-sm text-primary mb-2" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mb-0 text-xs">Generating document preview...</p>
              </div>

              <!-- Error State -->
              <div v-else-if="!preview_url" class="text-center py-5 text-xs text-muted">
                <p class="mb-0">No preview available</p>
              </div>

              <!-- DocuSeal Form Container -->
              <docuseal-form
                v-else
                :data-src="preview_url"
                data-preview="true">
              </docuseal-form>
            </div>

            <!-- Footer -->
            <div class="modal-footer border-0 p-2 d-flex justify-content-end">
              <button
                v-if="role !== 'Entity'"
                type="button"
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                @click.stop="confirm_send_contract(selected_template)"
              >
                Send Contract
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>
    <!-- /PREVIEW MODAL -->
  </div>
</div>